import React, { useState, useRef, useEffect } from 'react';
import MonthPicker from './MonthPicker';
import '../../../../styles/profile/components/add-certification-modal.css';

interface Certification {
  uid: string;
  certification_name: string;
  organization_name: string | null;
  issued_at: string;
  expires_at: string | null;
  credential_id: string | null;
  description: string | null;
}

interface AddCertificationModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: CertificationData) => void;
  title?: string;
  initialData?: any | null;
  isEditMode?: boolean;
}

interface CertificationData {
  uid?: string;
  certificationName: string;
  organization: string;
  issuedAt: string;
  expiresAt: string;
  credentialId: string;
  description: string;
}

export default function AddCertificationModal({ 
  open, 
  onClose, 
  onAdd,
  title = "Add Certification",
  initialData = null,
  isEditMode = false
}: AddCertificationModalProps) {
  const [certificationName, setCertificationName] = useState('');
  const [organization, setOrganization] = useState('');
  const [issuedAt, setIssuedAt] = useState<Date | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [showIssuedCalendar, setShowIssuedCalendar] = useState(false);
  const [showExpiresCalendar, setShowExpiresCalendar] = useState(false);
  const [credentialId, setCredentialId] = useState('');
  const [description, setDescription] = useState('');
  
  const overlayRef = useRef<HTMLDivElement>(null);

  const isFormValid = certificationName.trim() !== '';

  useEffect(() => {
    if (open && initialData) {
      setCertificationName(initialData.certification_name || '');
      setOrganization(initialData.organization_name || '');
      setIssuedAt(initialData.issued_at ? new Date(initialData.issued_at) : null);
      setExpiresAt(initialData.expires_at ? new Date(initialData.expires_at) : null);
      setCredentialId(initialData.credential_id || '');
      setDescription(initialData.description || '');
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) {
      setCertificationName('');
      setOrganization('');
      setIssuedAt(null);
      setExpiresAt(null);
      setShowIssuedCalendar(false);
      setShowExpiresCalendar(false);
      setCredentialId('');
      setDescription('');
    }
  }, [open]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ select date';
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const handleSubmit = () => {
    if (!certificationName.trim()) {
      return;
    }

    const data: CertificationData = {
      certificationName,
      organization,
      issuedAt: issuedAt ? issuedAt.toISOString() : '',
      expiresAt: expiresAt ? expiresAt.toISOString() : '',
      credentialId,
      description,
    };

    if (isEditMode && initialData?.uid) {
      data.uid = initialData.uid;
    }

    onAdd(data);
  };

  if (!open) return null;

  return (
    <div
      className="add-certification-modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="add-certification-modal">
        <div className="add-certification-modal-header">
          <h2 className="add-certification-modal-title">{title}</h2>
        </div>

        <div className="add-certification-modal-body">
          <div className="add-certification-modal-field-full">
            <label className="add-certification-modal-label">
              Certification Name
              <span className="add-certification-modal-required">Required</span>
            </label>
            <input
              type="text"
              className="add-certification-modal-input"
              placeholder="i.e. CPR Certification"
              value={certificationName}
              onChange={(e) => setCertificationName(e.target.value)}
            />
          </div>

          <div className="add-certification-modal-field-full">
            <label className="add-certification-modal-label">Organization</label>
            <input
              type="text"
              className="add-certification-modal-input"
              placeholder="i.e. American Heart Association"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
            />
          </div>

          <div className="add-certification-modal-grid">
            <div className="add-certification-modal-field">
              <label className="add-certification-modal-label">Issued At</label>
              <div className="add-certification-modal-date-wrapper">
                <button
                  type="button"
                  className="add-certification-modal-date-btn"
                  style={{ color: issuedAt ? '#1f2937' : '#9ca3af' }}
                  onClick={() => {
                    setShowIssuedCalendar(!showIssuedCalendar);
                    setShowExpiresCalendar(false);
                  }}
                >
                  {formatDate(issuedAt)}
                </button>
                {showIssuedCalendar && (
                  <MonthPicker
                    selectedDate={issuedAt}
                    onSelect={(date) => {
                      setIssuedAt(date);
                      setShowIssuedCalendar(false);
                    }}
                    onClose={() => setShowIssuedCalendar(false)}
                    maxDate={expiresAt || undefined}
                  />
                )}
              </div>
            </div>

            <div className="add-certification-modal-field">
              <label className="add-certification-modal-label">Expires At</label>
              <div className="add-certification-modal-date-wrapper">
                <button
                  type="button"
                  className="add-certification-modal-date-btn"
                  style={{ color: expiresAt ? '#1f2937' : '#9ca3af' }}
                  onClick={() => {
                    setShowExpiresCalendar(!showExpiresCalendar);
                    setShowIssuedCalendar(false);
                  }}
                >
                  {formatDate(expiresAt)}
                </button>
                {showExpiresCalendar && (
                  <MonthPicker
                    selectedDate={expiresAt}
                    onSelect={(date) => {
                      setExpiresAt(date);
                      setShowExpiresCalendar(false);
                    }}
                    onClose={() => setShowExpiresCalendar(false)}
                    minDate={issuedAt || undefined}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="add-certification-modal-field-full">
            <label className="add-certification-modal-label">Credential ID</label>
            <input
              type="text"
              className="add-certification-modal-input"
              placeholder="i.e. #HD343K2NE"
              value={credentialId}
              onChange={(e) => setCredentialId(e.target.value)}
            />
          </div>

          <div className="add-certification-modal-field-full">
            <label className="add-certification-modal-label">Description</label>
            <textarea
              className="add-certification-modal-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <div className="add-certification-modal-footer">
          <button
            type="button"
            className="add-certification-modal-btn add-certification-modal-btn--discard"
            onClick={onClose}
          >
            Discard
          </button>
          <button
            type="button"
            className="add-certification-modal-btn add-certification-modal-btn--add"
            onClick={handleSubmit}
            disabled={!isFormValid}
            style={{
              opacity: !isFormValid ? 0.5 : 1,
              cursor: !isFormValid ? 'not-allowed' : 'pointer'
            }}
          >
            {isEditMode ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

